import { Model } from '@/models/Model';
import { Attribute } from '@/models/Attribute';
import { Size } from '@/models/Size';
import { Color } from '@/models/Color';
import { Brand } from '@/models/Brand';
import { Category } from '@/models/Category';
import { Tag } from '@/models/Tag';
import { Supplier } from '@/models/Supplier';
import { Season } from '@/models/Season';
import { Material } from './Material';
import { Product } from './Product';
import { SupplierSize } from './SupplierSize';

export class ProductVariant extends Model {
  protected $name: string = 'ProductVariant';
  protected $endpoint: string = '/product-variants';

  protected $fillable: string[] = [
    'sizes',
    'colors',
    'brands',
    'out_of_stock',
    'min_price',
    'max_price',
    'comment',
    'is_approved',
    'approved',
    'retail_price',
    'size',
    'color',
    'brand',
    'block_date',
    'final_return_date',
    'minimum_stock_quantity',
    'is_rerun',
    'supplier',
    'supplier_code',
    'supplier_color',
    'supplier_color_code',
    'supplier_size',
    'supplier_upc',
    'ownership',
    'season',
    'description',
    'product_name',
    'name',
    'wholesale_price',
    'buy_price',
    'images',
    'upc',
    'status',
    'peoplevox_do_not_sync',
  ];

  public attachImage() {
    this.$endpoint = `/product-variants/attach-image`;

    return this;
  }

  public setDefaultImage(productId: string, payload: any) {
    const url = `/product-variants/${productId}/set-default-image`;
    return this.request('post', url, payload);
  }

  public detachImage(productId: string, payload: any) {
    const url = `/product-variants/${productId}/detach-image`;
    return this.request('delete', url, payload);
  }

  public addProductTag(productId: string, tagId: string) {
    const url = `/product-variants/${productId}/add-tag`;
    return this.request('post', url, { tag: tagId });
  }

  public removeProductTag(productId: string, tagId: string) {
    const url = `/product-variants/${productId}/remove-tag`;
    return this.request('delete', url, { tag: tagId });
  }

  public syncStock(productId: string) {
    const url = `/product-variants/${productId}/sync`;
    return this.request('post', url);
  }

  public attachCategory(productId: string, categoryId: string) {
    const url = `/products/${productId}/relationships/category/${categoryId}`;
    return this.request('post', url, {});
  }

  public returnToSupplier(payload: object) {
    const url = `/orders/return-to-supplier`;
    return this.request('post', url, payload);
  }

  public removeAttachedCategory(productId: string, categoryId: string) {
    const url = `/products/${productId}/relationships/category/${categoryId}`;
    return this.request('delete', url, {});
  }

  public deleteProduct(productId: string) {
    const url = `/product-variants/${productId}`;
    return this.request('delete', url, {});
  }

  // URLS for upload/import
  public importEans(): this {
    this.$endpoint = '/product-variants/ean/imports';
    return this;
  }

  // URLS for download/export
  public exportEans(): string {
    return `/v1${this.$endpoint}/ean/export`;
  }

  public description!: string;
  public sizes?: string;
  public size?: string | Size | null;
  public colors?: string;
  public color?: string | Color | null;
  public minimum_stock_quantity?: string;
  public is_rerun?: boolean;
  public supplier_size?: string | SupplierSize | null;
  public supplier_size_object?: string | SupplierSize | null;
  public supplier_color!: string;
  public supplier_color_code?: string;
  public supplier_code?: string;
  public supplier_style_name!: string;
  public supplier_style_code!: string;
  public supplier_upc?: string;
  public brands?: string;
  public block_date?: string | null;
  public final_return_date?: string | null;
  public brand?: string | Brand | null;
  public supplier?: string | Supplier | null;
  public season?: string | Season | null;
  public out_of_stock?: string;
  public ownership?: string;
  public min_price?: string;
  public max_price?: string;
  public images?: ProductVariantImage[];
  public approved?: boolean;
  public comment?: string;
  public id?: string;
  public related?: Product[];
  public stock?: number;
  public stock_on_hand?: number;
  public stock_purchase_order?: number;
  public attributes_count?: number;
  public product_name?: string;
  public name!: string;
  public is_approved?: boolean;
  public retail_price?: string;
  public wholesale_price?: string;
  public buy_price?: string;
  public product_uuid?: string;
  public upc?: string;
  public is_dummy_upc?: boolean;
  public sku?: string;
  public status?: string;
  public image_url?: string;
  public url_large?: string;
  public available_sizes?: string;
  public other_colors?: OtherColor[];
  public other_sizes?: OtherSize[];
  public attributes?: Attribute[];
  public categories?: Category[];
  public tags?: Tag[];
  public materials?: Material[];
  public peoplevox_do_not_sync?: boolean;
  public last_received_at?: string;
  public previously_ordered_product?: string | null;
}

export interface ProductVariantImage {
  id?: string;
  name?: string;
  url_large?: string;
  url_medium?: string;
  url_thumbnail?: string;
}

export enum ProductVariantIncludes {
  ATTRIBUTES = 'attributes',
  ATTRIBUTES_COUNT = 'attributes_count',
  AVAILABLE_SIZES = 'available_sizes',
  BRAND = 'brand',
  CATEGORIES = 'categories',
  COLOR = 'color',
  IMAGES = 'images',
  MATERIALS = 'materials',
  OTHER_COLORS = 'other_colors',
  OTHER_SIZES = 'other_sizes',
  PREVIOUSLY_ORDERED_PRODUCT = 'previously-ordered-product',
  RELATED = 'related',
  RESERVED_STOCK = 'reserved_stock',
  SEASON = 'season',
  SIZE = 'size',
  SUPPLIER_SIZE_OBJECT = 'supplier_size_object', // BE NEEDED WORKAROUND
  SUPPLIER = 'supplier',
  TAGS = 'tags',
}

export interface OtherColor {
  product_variant_id: string;
  color_id: string;
  name: string;
}

export interface OtherSize{
  product_variant_id: string;
  size_id: string;
  name: string;
}
